import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { DatePicker, message } from 'antd';
import Modal from 'common/components/modal';
import formatPrice from '~helpers/currency';
import { EXPORT_LEADGEN_ORDERS } from '../queries';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import csvExporter from '~helpers/csvExporter';
import { Hosts } from '~helpers/environments';
import { useTranslation } from 'react-i18next';
import { getCountryFromCode } from 'common/helpers/country';
import { pageType } from '~constants/pages';
import event from '~helpers/mixpanel';

const generatePropertyUrl = property => {
  const path = `${
    property.city && property.city.country && property.city.country.slug
      ? property.city.country.slug
      : ''
  }/${property.city && property.city.slug ? property.city.slug : ''}/p/${property.slug}`;

  return `${Hosts.propertyUrl}${path}`;
};

const uesStyles = makeStyles(
  () => ({
    wrapper: {
      '& .ant-modal-header': {
        padding: '32px 32px 24px 32px',
      },
      '& .ant-modal-body': {
        padding: '0 32px',
      },

      '& .ant-form-item-label': {
        lineHeight: '22px',
        marginBottom: 8,
      },
    },

    bodyRegular: {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.85)',
      lineHeight: '22px',
    },
  }),
  { name: 'SignmeUp-Dialog' },
);

const { RangePicker } = DatePicker;

function Index({ setOpen, open }) {
  const classes = uesStyles();
  const { t } = useTranslation();
  const [selectedDates, setSelectedDates] = React.useState([]);

  // createdAfter: $createdAfter;
  // createdBefore: $createdBefore;

  const [queryLeadOrders, { loading: leadOrdersLoading }] = useLazyQuery(EXPORT_LEADGEN_ORDERS, {
    fetchPolicy: 'no-cache',
    onCompleted: result => {
      const headers = {
        bookingNumber: 'Booking Number',
        property: 'Property',
        desiredMoveInDate: 'Desired Move-in Date',
        desiredMoveOutDate: 'Desired Move-out Date',
        tenancyLength: 'Tenancy Length',
        createDate: 'Create Date',
        legalFirstName: 'Legal First Name',
        legalLastName: 'Legal Last Name',
        email: 'Email',
        phone: 'Phone',
        identificationType: 'Identification Type',
        idNumber: 'ID Number',
        gender: 'Gender',
        dateOfBirth: 'Date of Birth',
        theSchoolIAmAboutToAttend: 'The School I am About to Attend',
        degree: 'Degree',
        specialRequirements: 'Special Requirements',
        nationality: 'Nationality/Region',
        notes: 'Notes',
        updateTime: 'Update Time',
        propertyName: 'Property Name',
        location: 'Location',
        entryToWebsiteLink: 'Entry to Website Link',
        roomName: 'Room Name (room type)',
        moveInAndMoveOutDate: 'Move-in & Move-out Date',
        totalPrice: 'Total Price',
        hash: 'Hash',
      };

      let data = result.viewer.company.orders.edges.map(item => {
        let node = item.node;
        const studentData = item.node?.studentData;
        let notes =
          node?.orderNotes?.map((m, idx) => `${idx + 1}${'.' + m.note}`).join('\n') ?? '-';
        return {
          bookingNumber: node?.referenceId ?? '-',
          property: node?.propertyName ?? '-',
          desiredMoveInDate: node?.moveInDate ?? '-',
          desiredMoveOutDate: node?.moveOutDate ?? '-',
          tenancyLength: node?.tenancyLength?.days + ' days',
          createDate: node?.createdAt ?? '-',
          legalFirstName: studentData?.passportFirstName ?? '-',
          legalLastName: studentData?.passportLastName ?? '-',
          email: studentData?.emailAddress ?? '-',
          phone: studentData?.phoneNumber ?? '-',
          identificationType: studentData?.documentType ?? '-',
          idNumber: studentData?.documentNumber ?? '-',
          gender: studentData?.gender ?? '-',
          dateOfBirth: studentData?.dateOfBirth ?? '-',
          theSchoolIAmAboutToAttend:
            studentData?.destinationUniversity ??
            t('landlord.booking.user_info.label.default_university'),
          degree: studentData?.studyMajor ?? '-',
          specialRequirements: studentData?.otherPreferences ?? '-',
          nationality: getCountryFromCode(studentData?.nationality),
          notes: notes,
          updateTime: studentData?.updatedAt ?? '-',
          propertyName: node?.propertyName ?? '-',
          location: `${node?.property?.city?.name}, ${node?.property?.city?.country?.name}`,
          entryToWebsiteLink: generatePropertyUrl(node?.property),
          roomName: node?.unitTypeName,
          moveInAndMoveOutDate:
            moment(node?.moveInDate).format('DD/MM/YYYY') +
            ' - ' +
            moment(node?.moveOutDate).format('DD/MM/YYYY'),
          totalPrice: formatPrice(node?.totalPrice, node?.currency) ?? '-',
          hash: node?.hash ?? '-',
        };
      });

      event('Exported List Leads', {
        'Start Date': moment(selectedDates[0]).format('YYYY-MM-DD'),
        'End Date': moment(selectedDates[1]).format('YYYY-MM-DD'),
        'Records Exported': data?.length ?? 0,
        'Current Page Type': pageType.bookingListPage,
      });

      if (data?.length > 0) {
        csvExporter(headers, data);
      } else {
        message.warning(t('cms:cms.global.export.no_data'));
      }
    },
    onError: e => {
      message.error(e.toString());
    },
  });

  const disabledDate = (current, selectedDates) => {
    // const startDate = moment('2022-01-01', 'YYYY-MM-DD');

    // if (current && current.isBefore(startDate)) {
    //   return true;
    // }

    // 如果已经有选择的日期，判断是否在90天内
    if (selectedDates.length > 0) {
      const [firstPicked, secondPicked] = selectedDates;
      if (firstPicked) {
        const minDate = firstPicked.clone().subtract(90, 'days');
        const maxDate = firstPicked.clone().add(90, 'days');
        return current && (current.isBefore(minDate) || current.isAfter(maxDate));
      }

      if (secondPicked && !firstPicked) {
        const minDate = secondPicked.clone().subtract(90, 'days');
        const maxDate = secondPicked.clone().add(90, 'days');
        return current && (current.isBefore(minDate) || current.isAfter(maxDate));
      }
    }

    return false;
  };

  return (
    <Modal
      width={446}
      visible={open}
      title={'Export'}
      okText={'Confirm'}
      cancelText="Cancel"
      wrapClassName={classes.wrapper}
      onCancel={() => {
        setOpen(false);
      }}
      onOk={() => {
        // setConfirmOpen(true);
        if (selectedDates?.length === 2) {
          if (Math.abs(selectedDates[0].diff(selectedDates[1], 'days')) <= 90) {
            queryLeadOrders({
              variables: {
                pageSize: 99999,
                createdAfter: moment(selectedDates[0]).format('YYYY-MM-DD'),
                createdBefore: moment(selectedDates[1]).format('YYYY-MM-DD'),
              },
            });
          } else {
            message.error('Please select a date range less than 90 days');
          }
        }
      }}
      okButtonProps={{
        loading: leadOrdersLoading,
        disabled: selectedDates?.length !== 2,
      }}
      destroyOnClose={true}
    >
      <div className="leadgen_export_content">
        <div className={classes.bodyRegular}>Date Range:</div>
        <RangePicker
          value={selectedDates}
          style={{ width: '100%', height: '32px', borderRadius: '2px', margin: '8px' }}
          format="ll"
          showToday={false}
          onChange={dates => setSelectedDates(dates)}
          disabledDate={current => disabledDate(current, selectedDates)}
        />
      </div>
    </Modal>
  );
}

export default Index;
